// The state of Settings → Data model lives in the URL, so the browser's Back button returns to the very
// same view (class, example column, which example, the id looked up, the field filter).

import { KIND_FILTERS, type KindFilter } from './fieldstats.ts';
import { VALUE_PAGE, VALUE_SORTS, type ValueSort } from './distinct.ts';
import { GROUP_BYS, NO_GROUP, SENSITIVITIES, type GroupBy } from './catalog.ts';

export type FieldSort = 'coverage' | 'name' | 'count' | 'last';
const SORTS: FieldSort[] = ['coverage', 'name', 'count', 'last'];
export const FIELD_PAGE = 200;

/** Which data models the class list shows, by their status in the catalog. */
export type ClassStatusFilter = 'all' | 'new' | 'checked' | 'deprecated';
export const CLASS_STATUS_FILTERS: ClassStatusFilter[] = ['all', 'new', 'checked', 'deprecated'];

export interface ModelView { cls?: string;
  /**
   * The class list's filters: status (`status=`) and the facet chips, each repeatable (OR within, AND across): domains
   * (`domain=`; an older link has one), sensitivities (`sens=`) and systems (`sys=`); "-" = none (catalog.ts NO_GROUP).
   */
  status: ClassStatusFilter; domain: string[]; sens: string[]; sys: string[];
  /** How the class list is grouped (`group=`) and the groups folded away (`fold=`, repeated; keys of catalog.ts groupClasses). */
  group: GroupBy; fold: string[];
  examples: boolean; index: number; find?: string; filter: string; show: KindFilter; sort: FieldSort; rows: number;
  /** The actual-data lookup is for this field's exact value (set when it comes from the distinct values). */
  ff?: string;
  /** The field whose distinct values are open, how they are sorted, filtered and how many are listed. */
  vf?: string; vs: ValueSort; vq: string; vn: number }

/** The values of a repeated parameter: no empties, no duplicates, in the order given. */
const many = (p: URLSearchParams, k: string) => [...new Set(p.getAll(k).filter(Boolean))];

export function readView(p: URLSearchParams): ModelView {
  const index = Number(p.get('ex'));
  return {
    cls: p.get('class') || undefined,
    status: CLASS_STATUS_FILTERS.find((k) => k === p.get('status')) ?? 'all',
    domain: many(p, 'domain'),
    sens: many(p, 'sens').filter((k) => k === NO_GROUP || (SENSITIVITIES as string[]).includes(k)),
    sys: many(p, 'sys'),
    group: GROUP_BYS.find((k) => k === p.get('group')) ?? 'none',
    fold: [...new Set(p.getAll('fold').filter(Boolean))],
    examples: p.get('examples') === '1',
    index: Number.isInteger(index) && index > 0 ? index : 0,
    find: p.get('find') || undefined,
    filter: p.get('filter') ?? '',
    show: KIND_FILTERS.find((k) => k === p.get('show')) ?? 'all',
    sort: SORTS.find((k) => k === p.get('sort')) ?? 'coverage',
    ff: p.get('ff') || undefined,
    vf: p.get('vf') || undefined,
    vs: VALUE_SORTS.find((k) => k === p.get('vs')) ?? 'common',
    vq: p.get('vq') ?? '',
    vn: Math.max(VALUE_PAGE, Math.min(5000, Math.round(Number(p.get('vn')) / VALUE_PAGE) * VALUE_PAGE || VALUE_PAGE)),
    rows: Math.max(FIELD_PAGE, Math.min(100_000, Math.round(Number(p.get('rows')) / FIELD_PAGE) * FIELD_PAGE || FIELD_PAGE)),
  };
}

/** The parameters with the view changed; everything else (env, model, …) stays. Defaults are left out of the URL. */
export function patchView(p: URLSearchParams, patch: Partial<ModelView>): URLSearchParams {
  const v = { ...readView(p), ...patch };
  const out = new URLSearchParams(p);
  const set = (k: string, value: string | undefined) => (value ? out.set(k, value) : out.delete(k));
  set('class', v.cls);
  set('status', v.status === 'all' ? undefined : v.status);
  for (const k of ['domain', 'sens', 'sys'] as const) {
    out.delete(k);
    for (const x of new Set(v[k])) if (x) out.append(k, x);
  }
  set('group', v.group === 'none' ? undefined : v.group);
  out.delete('fold');
  // Folded groups belong to the grouping: none without one
  if (v.group !== 'none') for (const k of new Set(v.fold)) out.append('fold', k);
  set('examples', v.examples ? '1' : undefined);
  set('ex', v.index > 0 ? String(v.index) : undefined);
  set('find', v.find);
  set('filter', v.filter || undefined);
  set('show', v.show === 'all' ? undefined : v.show);
  set('sort', v.sort === 'coverage' ? undefined : v.sort);
  set('rows', v.rows > FIELD_PAGE ? String(v.rows) : undefined);
  set('ff', v.find ? v.ff : undefined);
  set('vf', v.vf);
  set('vs', v.vs === 'common' ? undefined : v.vs);
  set('vq', v.vq || undefined);
  set('vn', v.vn > VALUE_PAGE ? String(v.vn) : undefined);
  return out;
}

/** The facets of the class list as the URL has them (catalog.ts listClasses / classFacets take it as it is). */
export const facetsOf = (v: ModelView) => ({ domain: v.domain, sens: v.sens, sys: v.sys });

/** Whether the class list is narrowed by anything (status or a chip): "Clear filters" shows then. */
export const isFiltered = (v: ModelView): boolean => v.status !== 'all' || v.domain.length + v.sens.length + v.sys.length > 0;

/** The patch that shows every class again. */
export const CLEAR_FILTERS: Partial<ModelView> = { status: 'all', domain: [], sens: [], sys: [] };

/** A chip clicked: its value added to the facet's values, or taken out when it was there. */
export function toggleFacet(v: ModelView, facet: 'domain' | 'sens' | 'sys', key: string): Partial<ModelView> {
  const list = v[facet];
  return { [facet]: list.includes(key) ? list.filter((k) => k !== key) : [...list, key] };
}

// ---------------------------------------------------------------- the two tabs of the page

/** The discovered data model (the classes) or the data models of your own. */
export type SettingsTab = 'data' | 'yours';

/** Parameters that belong to "Your data models": a link with one of them and no tab opens that tab (older links). */
const YOURS_KEYS = ['model', 'edit', 'import', 'sel', 'panel', 'sug'];

export function readTab(p: URLSearchParams): SettingsTab {
  const t = p.get('tab');
  if (t === 'yours') return 'yours';
  if (t === 'data') return 'data';
  return YOURS_KEYS.some((k) => p.get(k)) ? 'yours' : 'data';
}

/** The parameters with the other tab; everything else stays, so Back and switching back return to the same view. */
export function withTab(p: URLSearchParams, tab: SettingsTab): URLSearchParams {
  const out = new URLSearchParams(p);
  out.delete('tab');
  if (readTab(out) !== tab) out.set('tab', tab);
  return out;
}
